import { DOCUMENT } from '@angular/common';
import { computed, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { rejectFalsy } from '@improver/core';
import { LoggedUserService } from '@improver/infrastructure/auth-data-layer';
import { Language, LanguageService } from '@improver/infrastructure/intl-data-layer';
import { mergeMap, switchMap } from 'rxjs/operators';
import { MaintenanceGeneralIssueApiService } from '../maintenance-general-issue/maintenance-general-issue.api.service';

const TEST_ENVIRONMENT_HOSTNAMES = ['playground.testimprover.com', 'preprod-v8-4humanhrm.com'];
const MAINTENANCE_MESSAGE_DISMISSED_KEY = 'maintenance-message-dismissed';

@Injectable({ providedIn: 'root' })
export class ImproverLayoutGeneralIssueService {
  private readonly document = inject(DOCUMENT);
  private readonly languageService = inject(LanguageService);
  private readonly loggedUserService = inject(LoggedUserService);
  private readonly maintenanceGeneralIssueApiService = inject(MaintenanceGeneralIssueApiService);

  private readonly isMaintenanceMessageDismissed = signal(
    this.document.defaultView?.sessionStorage.getItem(MAINTENANCE_MESSAGE_DISMISSED_KEY) === 'true'
  );

  readonly isTestEnvironmentBannerVisible = computed(() => this.isCurrentTestEnvironment());
  readonly maintenanceMessage = toSignal(
    this.loggedUserService.getAuthorizedUserData().pipe(
      rejectFalsy(),
      switchMap(() => this.languageService.getCurrentLanguage()),
      rejectFalsy(),
      mergeMap((language: Language) => this.maintenanceGeneralIssueApiService.getGeneralIssue(language.code))
    )
  );
  readonly isMaintenanceMessageVisible = computed(() => !this.isMaintenanceMessageDismissed() && !!this.maintenanceMessage());

  dismissMaintenanceMessage(isDismissed: boolean) {
    if (isDismissed) {
      this.document.defaultView?.sessionStorage.setItem(MAINTENANCE_MESSAGE_DISMISSED_KEY, 'true');
      this.isMaintenanceMessageDismissed.set(true);
    }
  }

  private isCurrentTestEnvironment(): boolean {
    const currentHostname = this.document.location?.hostname;

    return (
      !!currentHostname &&
      TEST_ENVIRONMENT_HOSTNAMES.some(
        (testEnvironmentHostname) => currentHostname === testEnvironmentHostname || currentHostname.endsWith(`.${testEnvironmentHostname}`)
      )
    );
  }
}
